import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, Globe, TrendingUp, Users, Check, Phone,
  Star, Search, Zap, BarChart3, ChevronRight, Mail, Palette, X, Megaphone, Loader2, Send, Menu as MenuIcon,
  Facebook, Instagram, Sparkles, Clock, Eye, UserRound
} from "lucide-react";
import PromoBanner from "../components/PromoBanner";
import LaunchPromoSection from "../components/LaunchPromoSection";
import { usePromo, discountedPrice } from "../hooks/usePromo";

export const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=61560751643092",
  instagram: "https://www.instagram.com/monbeausite_/",
};

export function SocialLinks({ variant = "light", size = "md" }) {
  const base = variant === "dark"
    ? "text-white/60 hover:text-[#D4AF37] hover:bg-white/5"
    : "text-[#6B6B6B] hover:text-[#C46A4A] hover:bg-[#C46A4A]/5";
  const border = variant === "dark" ? "border-white/15" : "border-[#BFBFBF]/60";
  const iconSize = size === "sm" ? 16 : 18;
  const pad = size === "sm" ? "p-2" : "p-2.5";
  return (
    <div className="flex items-center gap-2.5" data-testid="social-links">
      <a
        href={SOCIAL_LINKS.facebook}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Suivez MonBeauSite sur Facebook"
        title="Facebook"
        data-testid="social-facebook-link"
        className={`rounded-full border ${border} ${pad} ${base} transition-all duration-300 hover:-translate-y-0.5`}
      >
        <Facebook size={iconSize} strokeWidth={1.8} />
      </a>
      <a
        href={SOCIAL_LINKS.instagram}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Suivez MonBeauSite sur Instagram"
        title="Instagram"
        data-testid="social-instagram-link"
        className={`rounded-full border ${border} ${pad} ${base} transition-all duration-300 hover:-translate-y-0.5`}
      >
        <Instagram size={iconSize} strokeWidth={1.8} />
      </a>
    </div>
  );
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { LANDING_FAQ_PICKS, getFaqPick, renderFaqAnswer } from "@/data/faq";
import { toast } from "sonner";
import api from "../lib/api";

const BANNER_URL = `/api/static/images/web/banniere_header.jpg`;
const HERO_URL = `/api/static/images/web/hero_studio_bg.jpg`;
const LOGO_URL = `/api/static/images/web/logo_transparent.png`;
const LOGO_BLANC_URL = `/api/static/images/web/logo_blanc.png`;

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function LandingPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [contactPrefill, setContactPrefill] = useState(null);
  const { state: promo } = usePromo();
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
        return () => clearTimeout(t);
      }
    }
  }, [location]);
  useEffect(() => {
    const handler = (e) => {
      setContactPrefill(e?.detail?.message || null);
      setContactOpen(true);
    };
    document.addEventListener("open-contact", handler);
    return () => document.removeEventListener("open-contact", handler);
  }, []);
  return (
    <div className="bg-[#FBF9F5] min-h-screen text-[#333333]">
      <Helmet>
        <title>MonBeauSite — Création de Site Internet Professionnel | Commerçants, Artisans, Restaurateurs</title>
        <meta name="description" content="MonBeauSite crée votre site internet professionnel sur-mesure. Création incluse, hébergement inclus, mise en ligne sous 10 jours. À partir de 39€/mois. La solution clé en main pour commerçants, artisans et restaurateurs partout en France." />
        <link rel="canonical" href="https://monbeausite.com/" />
        <meta name="keywords" content="création site internet professionnel, site web commerçant, site vitrine artisan, site restaurateur, agence web France, site internet clé en main, créer site internet pas cher, hébergement site web inclus" />
      </Helmet>
      <PromoBanner promo={promo} />
      <Navbar onContact={() => setContactOpen(true)} />
      <Hero onContact={() => setContactOpen(true)} />
      <TrustBanner />
      <ProblemSection />
      <LaunchPromoSection promo={promo} onContact={() => setContactOpen(true)} />
      <ServicesGrid onContact={() => setContactOpen(true)} promo={promo} variant="home" />
      <FreelanceSection />
      <TestimonialsSection />
      <FAQSection />
      <FinalCTA onContact={() => setContactOpen(true)} />
      <Footer />
      <ContactModal open={contactOpen} onClose={() => { setContactOpen(false); setContactPrefill(null); }} prefillMessage={contactPrefill} />
    </div>
  );
}

const NAV_LINKS = [
  { href: "/services-et-tarifs", label: "Services & Tarifs", isRoute: true },
  { href: "/#about", label: "À propos" },
  { href: "/#faq", label: "FAQ" },
  { href: "/blog", label: "Blog", isRoute: true },
  { label: "Contact", isAction: true, message: "Bonjour, je souhaite obtenir un renseignement / un devis personnalisé sur les forfaits MonBeauSite." },
];

export function Navbar({ onContact }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Smooth-scroll to a homepage section, even from another page
  const handleHashNav = (e, href) => {
    if (!href || !href.startsWith("/#")) return;
    e.preventDefault();
    const id = href.slice(2);
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else navigate(href);
    } else {
      navigate(href);
    }
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <>
      <nav className="fixed left-0 right-0 z-50 bg-[#FBF9F5]/92 backdrop-blur-md border-b border-[#BFBFBF]/60 transition-[top] duration-300" style={{ top: "var(--promo-h, 0px)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="navbar-logo" aria-label="MonBeauSite Home" className="flex items-center gap-2.5">
            <img
              src="/logo-mbs.png"
              alt="MonBeauSite"
              className="h-10 w-10 object-cover rounded-xl ring-1 ring-[#D4AF37]/20 shadow-sm"
            />
            <span className="hidden sm:inline-block text-[15px] font-semibold text-[#1F3535] tracking-tight font-body">
              MonBeauSite<span className="text-[#C46A4A]">.</span>com
            </span>
          </Link>

          {/* Desktop nav (shown at lg+ to prevent tablet cramping) */}
          <div className="hidden xl:flex items-center gap-10">
            {NAV_LINKS.map((l) => l.isRoute ? (
              <Link key={l.label} to={l.href} className="nav-text text-[#6B6B6B] hover:text-[#1F3535] transition-colors">{l.label}</Link>
            ) : l.isAction ? (
              <button
                key={l.label}
                type="button"
                onClick={() => document.dispatchEvent(new CustomEvent("open-contact", { detail: { message: l.message } }))}
                className="nav-text text-[#6B6B6B] hover:text-[#C46A4A] transition-colors"
                data-testid="navbar-contact-link"
              >
                {l.label}
              </button>
            ) : (
              <a key={l.label} href={l.href} onClick={(e) => handleHashNav(e, l.href)} className="nav-text text-[#6B6B6B] hover:text-[#1F3535] transition-colors">{l.label}</a>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="navbar-login-btn" className="hidden xl:inline-block">
              <Button variant="ghost" className="nav-text text-[#6B6B6B] hover:text-[#1F3535] hover:bg-[#2F4F4F]/5 h-9">
                Espace Client
              </Button>
            </Link>
            <Button onClick={onContact} data-testid="navbar-cta-btn" className="hidden xl:inline-flex bg-[#C46A4A] text-white hover:bg-[#A6553A] transition-colors text-xs font-semibold h-9 px-5 tracking-wider uppercase shadow-md shadow-[#C46A4A]/15">
              Lancer ma demo
            </Button>

            {/* Hamburger — phones AND tablets (both orientations) */}
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
              data-testid="mobile-menu-btn"
              className="xl:hidden w-10 h-10 rounded-xl flex items-center justify-center bg-[#2F4F4F] text-white hover:bg-[#1F3535] transition-colors ring-1 ring-[#D4AF37]/20"
            >
              <MenuIcon className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile / tablet drawer */}
      <div
        className={`xl:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!open}
        data-testid="mobile-menu-drawer"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-[#1F3535]/70 backdrop-blur-sm"
          onClick={closeMenu}
        />
        {/* Panel */}
        <div className={`absolute inset-x-0 top-0 bg-[#FBF9F5] shadow-2xl rounded-b-3xl border-b border-[#D4AF37]/30 transition-transform duration-300 ${open ? "translate-y-0" : "-translate-y-full"}`}>
          <div className="flex items-center justify-between h-16 px-5 border-b border-[#BFBFBF]/40">
            <div className="flex items-center gap-2.5">
              <img src="/logo-mbs.png" alt="MonBeauSite" className="h-10 w-10 object-cover rounded-xl ring-1 ring-[#D4AF37]/20" />
              <span className="text-[15px] font-semibold text-[#1F3535] tracking-tight font-body">
                MonBeauSite<span className="text-[#C46A4A]">.</span>com
              </span>
            </div>
            <button
              type="button"
              onClick={closeMenu}
              aria-label="Fermer le menu"
              data-testid="mobile-menu-close"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-[#1F3535] hover:bg-[#2F4F4F]/5 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="px-5 py-6 space-y-1" role="navigation" aria-label="Menu mobile">
            {NAV_LINKS.map((l) => l.isRoute ? (
              <Link
                key={l.label}
                to={l.href}
                onClick={closeMenu}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center justify-between py-3.5 px-4 rounded-xl text-[#1F3535] hover:bg-[#F2EEE8] transition-colors font-body font-medium border-b border-[#BFBFBF]/25 last:border-0"
              >
                <span>{l.label}</span>
                <ChevronRight className="w-4 h-4 text-[#C46A4A]" />
              </Link>
            ) : l.isAction ? (
              <button
                key={l.label}
                type="button"
                onClick={() => {
                  closeMenu();
                  document.dispatchEvent(new CustomEvent("open-contact", { detail: { message: l.message } }));
                }}
                data-testid="mobile-nav-contact"
                className="w-full flex items-center justify-between py-3.5 px-4 rounded-xl text-[#1F3535] hover:bg-[#F2EEE8] transition-colors font-body font-medium border-b border-[#BFBFBF]/25 last:border-0 text-left"
              >
                <span>{l.label}</span>
                <ChevronRight className="w-4 h-4 text-[#C46A4A]" />
              </button>
            ) : (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => { closeMenu(); handleHashNav(e, l.href); }}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+|&/g, '-')}`}
                className="flex items-center justify-between py-3.5 px-4 rounded-xl text-[#1F3535] hover:bg-[#F2EEE8] transition-colors font-body font-medium border-b border-[#BFBFBF]/25 last:border-0"
              >
                <span>{l.label}</span>
                <ChevronRight className="w-4 h-4 text-[#C46A4A]" />
              </a>
            ))}
          </nav>

          <div className="px-5 pb-6 pt-2 space-y-3">
            <Button
              onClick={() => { closeMenu(); onContact(); }}
              data-testid="mobile-cta-btn"
              className="w-full bg-[#C46A4A] text-white hover:bg-[#A6553A] h-12 text-sm font-semibold tracking-wider uppercase shadow-md shadow-[#C46A4A]/20"
            >
              <Send className="w-4 h-4 mr-2" />
              Lancer ma demo
            </Button>
            <Link to="/login" onClick={closeMenu} className="block">
              <Button variant="outline" className="w-full border-[#2F4F4F] text-[#2F4F4F] hover:bg-[#2F4F4F] hover:text-white h-11 text-sm font-medium tracking-wider uppercase">
                Espace Client
              </Button>
            </Link>
            <div className="pt-3 flex items-center justify-center gap-2 text-xs text-[#6B6B6B]">
              <span className="h-px w-8 bg-[#D4AF37]/50" />
              <a href="tel:+33652151096" className="font-body hover:text-[#C46A4A] transition-colors">06 52 15 10 96</a>
              <span className="h-px w-8 bg-[#D4AF37]/50" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Hero({ onContact }) {
  return (
    <section className="relative overflow-hidden min-h-[88vh] flex items-center pt-28 pb-20 lg:pt-32">
      {/* Full-bleed background image */}
      <div
        className="absolute inset-0 bg-cover bg-center animate-kenburns"
        style={{ backgroundImage: `url(${HERO_URL})` }}
        aria-hidden="true"
      />
      {/* Dark overlays for legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F1B16] via-[#132420]/92 to-[#1F3535]/55" aria-hidden="true" />
      <div className="absolute inset-0 bg-[#0F1B1A]/35" aria-hidden="true" />
      <div className="absolute inset-0 bg-tech-grid-dark opacity-70 pointer-events-none" aria-hidden="true" />

      <div className="max-w-6xl mx-auto px-6 relative w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-2xl"
        >
          <motion.div variants={fadeUp} className="mb-6 inline-flex items-center gap-2.5 bg-white/5 border border-white/15 rounded-full pl-2.5 pr-3.5 py-1.5">
            <span className="tech-pulse" aria-hidden="true" />
            <span className="font-mono-tech text-[10px] tracking-widest uppercase text-[#4FD8E8]">SYS_STATUS: OPERATIONAL</span>
          </motion.div>
          <motion.p variants={fadeUp} className="nav-text text-[#E0A17F] mb-6 flex items-center gap-2 font-mono-tech">
            <span className="w-8 h-px bg-[#C46A4A]" /> Studio de création visuelle
          </motion.p>
          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] mb-8 text-white font-montserrat tracking-tight"
          >
            Tout compris.{" "}
            <span className="font-playfair italic text-[#E8B14C] font-normal">Votre site web pro,</span>{" "}
            créé avec soin.
          </motion.h1>
          <motion.p variants={fadeUp} className="text-base sm:text-lg text-white/80 max-w-xl mb-10 leading-relaxed font-body font-light">
            Création incluse dans votre forfait (site jusqu'à 3 pages), hébergement tout inclus. Vous vous concentrez sur votre métier, je m'occupe du reste.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
            <a href="#services" data-testid="hero-cta-btn">
              <Button className="bg-[#C46A4A] text-white hover:bg-[#A6553A] transition-all h-12 px-8 text-sm font-medium tracking-wide uppercase shadow-lg shadow-[#C46A4A]/30">
                Découvrir les offres
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.6} />
              </Button>
            </a>
            <a href="#about">
              <Button variant="outline" className="border-white/70 text-white bg-transparent hover:bg-white hover:text-[#1F3535] hover:border-white transition-all h-12 px-8 text-sm font-medium tracking-wide uppercase">
                En savoir plus
              </Button>
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8 inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-[#D4AF37]/40 rounded-full pl-4 pr-5 py-2.5 shadow-lg shadow-black/10">
            <span className="text-[10px] tracking-widest uppercase text-white/60 font-body font-semibold">À partir de</span>
            <span className="text-xl font-semibold text-[#E8B14C] font-montserrat tracking-tight leading-none">39 €/mois</span>
            <span className="w-px h-6 bg-white/20" />
            <span className="text-[11px] text-white/75 font-body">Création &amp; hébergement inclus</span>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-6">
            <button
              type="button"
              data-testid="hero-quote-link"
              onClick={() => document.dispatchEvent(new CustomEvent("open-contact", { detail: { message: "Bonjour, je souhaite obtenir un renseignement / un devis personnalisé sur les forfaits MonBeauSite." } }))}
              className="text-sm text-white/70 hover:text-white transition-colors font-body inline-flex items-center gap-1.5 group"
            >
              Une simple question, un devis sur-mesure ?
              <span className="text-[#E8B14C] font-semibold underline underline-offset-4 decoration-[#D4AF37]/60 group-hover:decoration-[#E8B14C]">Demandez un renseignement</span>
              <ArrowRight className="w-3.5 h-3.5 text-[#E8B14C] group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBanner() {
  const stats = [
    { value: "72%", label: "des consommateurs cherchent en ligne" },
    { value: "3x", label: "plus de contacts avec un site" },
    { value: "24/7", label: "votre vitrine ne ferme jamais" },
    { value: "10 jours", label: "pour votre site en ligne" },
  ];
  return (
    <section className="py-8 border-y border-[#BFBFBF] bg-white relative overflow-hidden" data-testid="trust-banner">
      <div className="absolute inset-0 bg-tech-grid opacity-50 pointer-events-none" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-6 relative flex flex-wrap justify-center items-center gap-y-6">
        {stats.map((s, i) => (
          <div key={i} className="flex items-center">
            <div className="text-center px-6 lg:px-12">
              <p className="text-2xl font-medium text-[#2F4F4F] font-mono-tech tracking-tight">{s.value}</p>
              <p className="text-[11px] text-[#6B6B6B] mt-1 font-mono-tech uppercase tracking-wider">{s.label}</p>
            </div>
            {i < stats.length - 1 && <span className="hidden sm:block w-px h-10 bg-gradient-to-b from-transparent via-[#008CA8]/40 to-transparent" aria-hidden="true" />}
          </div>
        ))}
      </div>
    </section>
  );
}

function ProblemSection() {
  return (
    <section className="py-24 lg:py-32 bg-[#F2EEE8]/60">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center"
        >
          <motion.div variants={fadeUp}>
            <p className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-5 inline-flex">
              01 // LE CONSTAT
            </p>
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight mb-6 text-[#1F3535] font-montserrat tracking-tight">
              Chaque jour sans site,{" "}
              <span className="font-playfair italic text-[#C46A4A] font-normal">c'est du chiffre d'affaires en moins.</span>
            </h2>
            <p className="text-[#6B6B6B] leading-relaxed mb-8 font-body font-light">
              Vos clients potentiels vous cherchent sur Google. S'ils ne vous trouvent pas, ils vont chez la concurrence. Un artisan, un restaurant, un commerce : peu importe votre secteur, votre absence en ligne vous coûte de l'argent chaque jour.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} className="grid grid-cols-2 gap-4">
            {[
              { icon: BarChart3, title: "46%", desc: "des recherches Google sont locales" },
              { icon: Users, title: "88%", desc: "consultent un site avant d'acheter" },
              { icon: TrendingUp, title: "+67%", desc: "de CA en moyenne avec un site" },
              { icon: Globe, title: "24/7", desc: "votre vitrine ne ferme jamais" },
            ].map((item, i) => (
              <div key={i} className="tech-bracket bg-white border border-[#BFBFBF]/40 p-6 rounded-lg hover-lift hover:border-[#008CA8]/50 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <item.icon className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.5} />
                  <span className="font-mono-tech text-[10px] tracking-widest text-[#008CA8]/70">0{i + 1}</span>
                </div>
                <p className="text-3xl font-semibold text-[#C46A4A] font-mono-tech">{item.title}</p>
                <p className="text-xs text-[#6B6B6B] mt-2 font-body leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function DetailModal({ item, onClose, onContact }) {
  if (!item) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-[#BFBFBF]"
          onClick={e => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-[#BFBFBF] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div>
              <h3 className="text-lg font-semibold text-[#2F4F4F] font-display">{item.title}</h3>
              <p className="text-sm font-semibold text-[#3F6B6B] font-body">{item.price}</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F5F5F0] hover:bg-[#BFBFBF] flex items-center justify-center transition-colors" data-testid="detail-modal-close">
              <X className="w-4 h-4 text-[#555555]" />
            </button>
          </div>
          <div className="px-6 py-5 space-y-5">
            {item.detail?.tagline && (
              <p className="text-sm text-[#3F6B6B] italic font-playfair">{item.detail.tagline}</p>
            )}
            {item.detail?.intro && (
              <p className="text-sm text-[#555555] leading-relaxed font-body">{item.detail.intro}</p>
            )}
            {item.detail?.sections?.map((section, i) => (
              <div key={i}>
                <h4 className="text-xs uppercase tracking-widest text-[#3F6B6B] font-semibold mb-2 font-body">{section.title}</h4>
                {section.text && <p className="text-sm text-[#555555] leading-relaxed mb-2 font-body">{section.text}</p>}
                {section.items && (
                  <div className="space-y-2">
                    {section.items.map((it, j) => (
                      <div key={j} className="flex items-start gap-2 text-sm text-[#555555] font-body">
                        <Check className="w-3.5 h-3.5 text-[#3F6B6B] mt-0.5 flex-shrink-0" />
                        <span>{it}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {item.detail?.items && !item.detail?.sections && (
              <div className="space-y-3">
                {item.detail.items.map((it, j) => (
                  <div key={j} className="flex items-start gap-2 text-sm text-[#555555] font-body">
                    <Check className="w-3.5 h-3.5 text-[#3F6B6B] mt-0.5 flex-shrink-0" />
                    <span>{it}</span>
                  </div>
                ))}
              </div>
            )}
            {item.detail?.engagement && (
              <div className="bg-[#3F6B6B]/5 border border-[#3F6B6B]/15 rounded-lg p-3">
                <p className="text-xs text-[#2F4F4F] font-semibold font-body">Engagement : {item.detail.engagement}</p>
              </div>
            )}
          </div>
          <div className="px-6 pb-5">
            <Button
              onClick={() => {
                onClose();
                if (item.ctaMessage) {
                  // Open contact modal with prefilled message via custom event
                  document.dispatchEvent(new CustomEvent("open-contact", { detail: { message: item.ctaMessage } }));
                } else {
                  onContact();
                }
              }}
              className="w-full bg-[#C46A4A] text-white hover:bg-[#A6553A] h-11 font-medium tracking-wide uppercase text-xs shadow-md shadow-[#C46A4A]/20"
              data-testid="detail-modal-cta"
            >
              <Send className="mr-2 w-4 h-4" /> {item.ctaLabel || "Lancer ma demo gratuite"}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function ServicesGrid({ onContact, promo, variant = "full" }) {
  const [detail, setDetail] = useState(null);
  const promoActive = !!promo?.active;
  const promoPct = promo?.discount_pct || 0;

  const plans = [
    {
      icon: Zap,
      title: "La Vitrine Active",
      price: "39 EUR Net/mois",
      basePrice: 39,
      tagline: "Votre vitrine, toujours à jour.",
      desc: "L'offre clé en main vivante et autonome pour les artisans et commerçants.",
      features: [
        "Conception & Design sur-mesure ultra-rapide",
        "Contenu dynamique autonome (Menu, tarifs, dates de concerts ou actus modifiables en 1 clic via Google Sheet)",
        "Responsive Design parfait (Mobile, Tablette, PC)",
        "Hébergement haute performance & Nom de domaine inclus",
        "Certificat SSL & Sécurité gérée",
        "Référencement local (SEO & fiche Google optimisée)",
        "Mises à jour & Maintenance technique incluses",
        "Zéro frais de création & Engagement initial de 6 mois"
      ],
      accent: true,
      badge: "Recommandé",
      detail: {
        tagline: "Votre vitrine, toujours à jour.",
        intro: "L'offre clé en main pour les artisans et commerçants : un site sur-mesure, vivant et autonome, que vous mettez à jour en un clic — sans toucher au code et sans frais cachés.",
        sections: [
          { title: "Ce qui est inclus", items: [
            "Conception & Design sur-mesure ultra-rapide : un site vitrine unique (One Page efficace ou petite structure jusqu'à 3 pages), pensé pour votre activité et mis en ligne rapidement.",
            "Contenu dynamique autonome : modifiez vos menus, tarifs, dates de concerts ou actualités en 1 clic, directement depuis un simple Google Sheet — sans jamais toucher au code.",
            "Responsive Design parfait : un rendu impeccable sur mobile, tablette et ordinateur.",
            "Hébergement haute performance & Nom de domaine inclus : toute la technique est gérée pour vous.",
            "Certificat SSL & Sécurité gérée : connexion sécurisée (HTTPS) et sécurité assurée en continu.",
            "Référencement local : site optimisé pour le SEO dès le départ et fiche Google Business Profile tenue à jour (photos, horaires, infos clés).",
            "Mises à jour & Maintenance technique incluses : votre site reste rapide, sûr et à jour.",
            "Corrections de texte gratuites et illimitées, et 1 correction mineure incluse par mois (remplacement de photo existante, mise à jour d'horaires, petit ajustement visuel).",
            "Zéro frais de création & Engagement initial de 6 mois : vous démarrez sans coût initial, avec un engagement de 6 mois puis une reconduction mensuelle résiliable à tout moment."
          ]},
          { title: "Gestion des évolutions", text: "Tout ajout de nouveau contenu ou nouvelle section est réalisé au temps passé.", items: ["Tarif : 75 EUR Net / heure", "Souplesse : facturation sur devis par tranches de 15 minutes (soit 18,75 EUR la petite évolution)"] }
        ],
        engagement: "Engagement initial de 6 mois, puis reconduction mensuelle résiliable à tout moment."
      }
    },
    {
      icon: TrendingUp,
      title: "La Formule Croissance",
      price: "59 EUR Net/mois",
      basePrice: 59,
      tagline: "Fidélisez votre clientèle et automatisez votre communication.",
      desc: "Pour les entreprises qui souhaitent fidéliser leur clientèle et automatiser leur communication.",
      features: ["Gestion du contenu via Dashboard administrateur dédié (au lieu du Google Sheet)", "Module de collecte & gestion de fichier clients (RGPD)", "E-mailing & Newsletter (jusqu'à 300 mails/jour)", "Boîte e-mail professionnelle personnalisée", "Prise de rendez-vous, réservation, devis avancé ou e-commerce (sur devis)"],
      inheritsEssential: true,
      detail: {
        tagline: "Fidélisez votre clientèle et automatisez votre communication.",
        intro: "Pour les entreprises qui veulent aller plus loin : toute La Vitrine Active, enrichie d'outils de fidélisation et d'automatisation de votre communication.",
        sections: [
          { title: "Tout ce qui est inclus dans La Vitrine Active, et en plus", items: [
            "Gestion du contenu via un Dashboard administrateur dédié, intégré à votre site : vous pilotez tous vos contenus depuis un espace centralisé et sécurisé, en remplacement du Google Sheet de La Vitrine Active.",
            "Module de collecte et gestion de fichier clients : base de contacts sécurisée et en conformité RGPD (registre des traitements inclus).",
            "Intégration e-mailing & Newsletter : jusqu'à 300 mails/jour pour informer et fidéliser votre clientèle.",
            "Boîte e-mail professionnelle personnalisée (ex : contact@votre-domaine.com).",
            "Intégration d'outils de prise de rendez-vous, de réservation en ligne, de devis avancé ou de module e-commerce : développés sur devis selon vos besoins."
          ]},
          { title: "Évolutions & Accompagnement", text: "Parce que votre entreprise grandit, votre site doit pouvoir suivre. Je reste votre architecte dédié.", items: [
            "Évolutions majeures : toute refonte structurelle, nouveau design de section ou ajout de page complexe.",
            "Modules avancés (prise de rendez-vous, réservation, devis, e-commerce, gestion clientèle) : développables sur devis, facturés au tarif horaire de 75 EUR Net.",
            "Souplesse maximale : facturation au temps passé, par tranches de 15 minutes."
          ]}
        ],
        engagement: "Engagement initial de 6 mois, puis reconduction mensuelle résiliable à tout moment."
      }
    },
  ];

  const options = [
    {
      icon: Megaphone,
      title: "Fidélité & Marketing +",
      price: "+29 EUR/mois",
      tagline: "Votre fichier client s'est bien développé ?",
      desc: "Passez à la vitesse supérieure : newsletter de masse pour entreprises en croissance, en complément des fonctionnalités déjà incluses dans La Formule Croissance.",
      features: ["Newsletter de masse : 5 000 mails/mois inclus (jusqu'à 50 000)", "Module de gestion clientèle avancé (sur devis)"],
      detail: {
        tagline: "Votre fichier client s'est bien développé ?",
        intro: "L'option idéale pour booster vos campagnes marketing et fidéliser massivement votre communauté. Elle vient en complément des fonctionnalités déjà incluses dans votre forfait La Formule Croissance.",
        items: [
          "Volume étendu : newsletter de masse de 5 000 mails par mois inclus, extensible jusqu'à 50 000 mails par mois selon vos besoins.",
          "Outils de Gestion : accès au développement sur votre site d'un module de gestion clientèle ou de prise de rendez-vous, facturé au tarif horaire de 75 EUR Net après devis."
        ]
      }
    },
  ];

  const branding = [
    {
      icon: Palette,
      title: "Le Logo Seul",
      price: "150 EUR",
      tagline: "Pour les marques qui ont déjà une charte graphique établie.",
      desc: "Un logo unique et épuré, aligné sur votre charte existante.",
      ctaLabel: "Me contacter pour un logo",
      ctaMessage: "Bonjour, je souhaite être contacté(e) pour la création d'un logo (Studio Branding — Le Logo Seul, 150 EUR). J'ai déjà ma charte graphique / typographies / palette de couleurs établies.",
      detail: {
        tagline: "Le signe de votre marque, pensé pour durer.",
        intro: "Un logo épuré, identifiable au premier coup d'œil. Conçu à la main, dessiné pour vous, livré prêt à l'emploi sur tous vos supports — du tampon au site web, du flyer au profil LinkedIn.",
        sections: [
          {
            title: "⚠️ Prérequis important — pour qui ?",
            items: [
              "Vous avez déjà une identité visuelle établie (couleurs, typographies, ambiance)",
              "Vous disposez d'une charte graphique — même sommaire — sur laquelle je peux m'appuyer",
              "Vous savez précisément ce que vous voulez visuellement transmettre",
              "Vous cherchez uniquement à ajouter (ou refondre) LE symbole de votre marque",
              "Si ce n'est pas votre cas, orientez-vous plutôt vers le Pack Identité Visuelle qui construit tout l'univers de A à Z."
            ]
          },
          {
            title: "Ce que vous recevez",
            items: [
              "Un logo principal vectoriel (parfaitement net à toutes les tailles)",
              "Une déclinaison monochrome (noir et blanc)",
              "Tous les formats nécessaires : SVG, PNG, JPG, PDF — fonds transparents inclus",
              "Une mini-charte d'utilisation : tailles minimales, espaces de respiration, fonds autorisés"
            ]
          },
          {
            title: "Le processus",
            items: [
              "Brief d'1h pour comprendre votre univers et votre charte existante",
              "Présentation de 2 à 3 pistes graphiques sous 7 jours",
              "Affinage et finalisation de la version retenue",
              "Livraison de tous les fichiers sources"
            ]
          }
        ]
      }
    },
    {
      icon: Palette,
      title: "Pack Identité Visuelle",
      price: "350 EUR",
      tagline: "Vous partez de zéro ? Je crée tout l'univers de votre marque.",
      desc: "Logo, palette, typographies et templates réseaux sociaux — l'univers complet.",
      ctaLabel: "Me contacter pour un pack complet",
      ctaMessage: "Bonjour, je souhaite être contacté(e) pour un Pack Identité Visuelle complet (Studio Branding — Pack Identité Visuelle, 350 EUR). Je pars de zéro / je n'ai pas encore de charte graphique établie.",
      detail: {
        tagline: "Tout l'univers visuel de votre marque, cohérent et clé en main.",
        intro: "Le pack idéal si vous partez de zéro (pas de charte, pas de typos définies, pas de palette officielle) ou si votre identité actuelle a besoin d'être refondue entièrement. Je construis tout le système visuel — logo, couleurs, typographies, templates — pour que votre communication soit cohérente et professionnelle sur tous vos points de contact.",
        sections: [
          {
            title: "✓ Pour qui ce pack ?",
            items: [
              "Vous démarrez votre activité et vous n'avez pas encore d'identité visuelle",
              "Votre identité actuelle est incohérente ou dépassée et vous voulez tout reprendre",
              "Vous n'avez pas de charte graphique écrite, ni de typographies officielles définies",
              "Vous cherchez un ensemble cohérent (logo + couleurs + typos) plutôt qu'un logo isolé"
            ]
          },
          {
            title: "Ce qui est inclus",
            items: [
              "Logo principal + déclinaisons (couleur, monochrome, favicon)",
              "Palette de couleurs personnalisée (4 à 6 teintes), avec codes hex pour le web et CMJN pour l'impression",
              "Sélection typographique : 1 typo de titre + 1 typo de sous-titre + 1 typo de texte, libres de droits",
              "3 templates de posts Instagram / Facebook prêts à éditer (Canva)",
              "1 template de bannière LinkedIn / Facebook / X",
              "Tous les fichiers sources et exports (SVG, PNG, JPG, PDF)",
              "Mini-guide d'utilisation (5 pages) pour appliquer votre identité partout"
            ]
          },
          {
            title: "Le processus",
            items: [
              "Brief approfondi : positionnement, valeurs, audience, références visuelles",
              "Moodboard de validation avant production",
              "Présentation et ajustements en visio",
              "Livraison sous 14 jours après validation du logo"
            ]
          }
        ]
      }
    },
    {
      icon: Mail,
      title: "Supports Imprimés",
      price: "Sur devis",
      tagline: "Cartes de visite, flyers, menus papier — l'identité prolongée.",
      desc: "Cartes de visite, flyers ou menus papier.",
      ctaLabel: "Demander un devis",
      ctaMessage: "Bonjour, je souhaite recevoir un devis pour des supports imprimés (Studio Branding — Supports Imprimés). Voici mon besoin :",
      detail: {
        tagline: "Le prolongement physique de votre élégance numérique.",
        intro: "Une fois votre identité visuelle posée, je conçois pour vous tous les supports physiques qui font la différence en boutique, en rendez-vous client ou en distribution.",
        sections: [
          {
            title: "Ce que je peux concevoir pour vous",
            items: [
              "Cartes de visite — papier de qualité, finitions au choix (mat, brillant, satiné, dorure)",
              "Flyers et tracts pour vos opérations promotionnelles",
              "Menus de restaurant — formats classiques, sets de table, ardoises personnalisées",
              "Affiches A4/A3 pour vos vitrines",
              "Étiquettes produit, kakémonos, roll-up de salon",
              "Documents commerciaux : devis, factures, plaquettes de présentation"
            ]
          },
          {
            title: "Comment ça marche",
            items: [
              "Brief de votre besoin (volume, format, support)",
              "Devis personnalisé sous 48h, incluant la conception ET l'impression si vous le souhaitez",
              "Maquettes proposées en 5 à 7 jours",
              "Aller-retour pour ajustements",
              "Livraison directe chez l'imprimeur partenaire ou fichiers prêts à imprimer"
            ]
          }
        ]
      }
    },
  ];

  return (
    <section id="services" className="py-24 lg:py-32 bg-[#F2EEE8]/40 border-y border-[#BFBFBF]/40 relative">
      {/* Subtle gold divider at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-20">
            <motion.p variants={fadeUp} className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-5 inline-flex justify-center">
              02 // SERVICES &amp; TARIFS
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-medium mb-5 text-[#1F3535] font-montserrat tracking-tight">
              Votre site web, <span className="font-playfair italic text-[#C46A4A] font-normal">sans compromis.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#6B6B6B] max-w-lg mx-auto leading-relaxed font-body font-light">
              Pour moins de 2 EUR par jour, votre entreprise est visible 24h/24 sur Internet.
              Création incluse dans votre forfait (jusqu'à 3 pages), sans frais de lancement.
            </motion.p>
          </motion.div>

          {/* Forfaits */}
          <div className="grid md:grid-cols-2 gap-6 mb-24">
            {plans.map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                onClick={() => setDetail(s)}
                data-testid={`plan-card-${i}`}
                className={`group relative overflow-hidden rounded-2xl p-8 hover-lift cursor-pointer transition-all duration-300 ${
                  s.accent
                    ? "tech-bracket tech-glow bg-[#1F3535] text-white shadow-xl shadow-[#1F3535]/20 ring-1 ring-[#00E5FF]/30"
                    : "bg-white border border-[#BFBFBF]/50 hover:border-[#008CA8]/50 shadow-sm"
                }`}
              >
                {/* Decorative corner for accent card */}
                {s.accent && (
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[radial-gradient(circle_at_top_right,rgba(0,229,255,0.12),transparent_70%)] pointer-events-none" aria-hidden="true" />
                )}
                {s.badge && (
                  <span className={`absolute top-4 right-4 text-white text-[10px] px-3 py-1 rounded-full font-semibold nav-text tracking-wider uppercase shadow-md ${s.accent ? "bg-[#C46A4A] shadow-[#C46A4A]/30" : "bg-[#2F4F4F]"}`}>
                    {s.badge}
                  </span>
                )}
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${s.accent ? "bg-[#C46A4A]/20 border border-[#C46A4A]/40" : "bg-[#C46A4A]/10"}`}>
                      <s.icon className={`w-4 h-4 ${s.accent ? "text-[#E2A38C]" : "text-[#C46A4A]"}`} strokeWidth={1.6} />
                    </div>
                    <span className={`font-mono-tech text-[10px] tracking-widest px-2 py-0.5 rounded border ${s.accent ? "text-[#4FD8E8] border-[#00E5FF]/40 bg-[#00E5FF]/5" : "text-[#008CA8] border-[#008CA8]/30 bg-[#008CA8]/5"}`} data-testid={`plan-version-${i}`}>
                      v{i + 1}.0
                    </span>
                    {promoActive && s.basePrice ? (
                      <div className="flex items-center gap-2" data-testid={`plan-promo-price-${i}`}>
                        <span className={`text-xs font-mono-tech line-through decoration-2 ${s.accent ? "text-white/40 decoration-[#E2A38C]/60" : "text-[#999]/80 decoration-[#C46A4A]/50"}`}>
                          {s.basePrice} €
                        </span>
                        <span className={`text-base font-semibold font-mono-tech ${s.accent ? "text-[#E2A38C]" : "text-[#C46A4A]"}`}>
                          {discountedPrice(s.basePrice, promoPct)} €
                        </span>
                        <span className={`text-[10px] font-body ${s.accent ? "text-white/60" : "text-[#6B6B6B]"}`}>/mois</span>
                        <span className={`text-[9px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${s.accent ? "bg-[#D4AF37] text-[#1F3535]" : "bg-[#1F3535] text-[#D4AF37]"}`}>
                          −{promoPct}% · 1 an
                        </span>
                      </div>
                    ) : (
                      <span className={`text-sm font-medium font-mono-tech ${s.accent ? "text-[#E2A38C]" : "text-[#C46A4A]"}`}>{s.price}</span>
                    )}
                  </div>
                  <h3 className={`text-2xl font-medium mb-2 font-montserrat tracking-tight ${s.accent ? "text-white" : "text-[#1F3535]"}`}>{s.title}</h3>
                  <p className={`text-sm italic mb-4 font-playfair ${s.accent ? "text-[#E8D8A9]" : "text-[#3F6B6B]"}`}>{s.tagline}</p>
                  <p className={`text-sm mb-6 leading-relaxed font-body ${s.accent ? "text-white/90" : "text-[#4A4A4A]"}`}>{s.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {s.inheritsEssential && (
                      <span
                        data-testid="inherits-essential-pill"
                        className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-body w-full justify-start ${
                          s.accent
                            ? "bg-[#D4AF37]/15 border border-[#D4AF37]/45 text-[#E8D8A9]"
                            : "bg-[#1F3535]/5 border border-[#1F3535]/15 text-[#1F3535]"
                        }`}
                      >
                        <Sparkles className={`w-3 h-3 flex-shrink-0 ${s.accent ? "text-[#D4AF37]" : "text-[#C46A4A]"}`} strokeWidth={2} />
                        <span className="font-semibold tracking-wide">Tout ce qu'inclut <span className="font-playfair italic font-normal">La Vitrine Active</span>, et en plus :</span>
                      </span>
                    )}
                    {s.features.map((f, j) => (
                      <span key={j} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-body transition-colors ${
                        s.accent
                          ? "bg-white/10 border border-[#D4AF37]/30 text-white"
                          : "bg-[#FBF9F5] border border-[#BFBFBF]/50 text-[#1F3535] group-hover:border-[#D4AF37]/50"
                      }`}>
                        <Check className={`w-3 h-3 ${s.accent ? "text-[#D4AF37]" : "text-[#C46A4A]"}`} />{f}
                      </span>
                    ))}
                  </div>
                  {s.note && <p className={`text-xs italic mt-3 font-body ${s.accent ? "text-white/60" : "text-[#555555]"}`}>{s.note}</p>}
                  <p className={`text-xs font-semibold mt-5 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 font-body tracking-wide uppercase ${s.accent ? "text-[#E8D8A9]" : "text-[#C46A4A]"}`}>
                    En savoir plus <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {variant === "home" && (
            <motion.div variants={fadeUp} className="text-center mb-10">
              <Link
                to="/services-et-tarifs"
                data-testid="see-all-offers-btn"
                className="inline-flex items-center gap-2 bg-[#1F3535] text-white hover:bg-[#0F2424] px-7 py-3.5 rounded-full text-sm font-semibold font-body nav-text tracking-wide uppercase group transition-colors shadow-md shadow-[#1F3535]/20"
              >
                Voir le détail des offres
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <p className="text-xs text-[#6B6B6B] mt-3 font-body">Comparatif complet, options & studio branding sur la page dédiée.</p>
            </motion.div>
          )}

          {variant === "full" && (<>
          {/* Comparison table — Active vs Croissance */}
          <motion.div variants={fadeUp} className="mb-16" data-testid="plans-comparison">
            <div className="text-center mb-8">
              <p className="section-num font-mono-tech text-xs tracking-widest text-[#008CA8] mb-4 inline-flex justify-center">
                COMPARATIF // EN UN COUP D'ŒIL
              </p>
              <h3 className="text-2xl sm:text-3xl font-medium text-[#1F3535] font-montserrat tracking-tight">
                Active ou <span className="font-playfair italic text-[#C46A4A] font-normal">Croissance</span> ?
              </h3>
            </div>
            <div className="tech-bracket max-w-3xl mx-auto overflow-hidden rounded-2xl border border-[#BFBFBF]/50 bg-white shadow-sm">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-[#1F3535] text-white">
                    <th className="py-4 px-4 sm:px-6 font-mono-tech text-[10px] sm:text-[11px] uppercase tracking-widest text-white/60 font-medium align-bottom">
                      Caractéristiques
                    </th>
                    <th className="py-4 px-2 sm:px-4 text-center border-l border-white/10 bg-[#163029] relative">
                      <span className="absolute top-0 left-0 right-0 h-0.5 bg-[#00E5FF]" aria-hidden="true" />
                      <span className="block font-montserrat font-semibold text-sm sm:text-base leading-tight">La Vitrine<br className="sm:hidden" /> Active</span>
                      <span className="block font-mono-tech text-[10px] text-[#4FD8E8] mt-1">39 €/mois</span>
                    </th>
                    <th className="py-4 px-2 sm:px-4 text-center border-l border-white/10">
                      <span className="block font-montserrat font-semibold text-sm sm:text-base leading-tight">La Formule<br className="sm:hidden" /> Croissance</span>
                      <span className="block font-mono-tech text-[10px] text-[#E8B14C] mt-1">59 €/mois</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { label: "Design sur-mesure & responsive", active: true, croissance: true },
                    { label: "Hébergement, nom de domaine & SSL inclus", active: true, croissance: true },
                    { label: "Gestion du contenu dynamique", method: true, active: "Via Google Sheet", croissance: "Dashboard dédié" },
                    { label: "Référencement local (SEO & fiche Google)", active: true, croissance: true },
                    { label: "Corrections illimitées + 1 mineure / mois", active: true, croissance: true },
                    { label: "Engagement initial de 6 mois", active: true, croissance: true },
                    { label: "Fichier clients (conformité RGPD)", active: false, croissance: true },
                    { label: "E-mailing & newsletter (jusqu'à 300 mails/jour)", active: false, croissance: true },
                    { label: "Boîte e-mail professionnelle personnalisée", active: false, croissance: true },
                    { label: "Prise de rendez-vous, réservation, devis avancé ou e-commerce (sur devis)", active: false, croissance: true },
                    { label: "Option Fidélité & Marketing + disponible", active: false, croissance: true },
                  ].map((row, ri) => (
                    <tr key={ri} className={`border-t border-[#BFBFBF]/25 ${ri % 2 === 1 ? "bg-[#FBF9F5]/60" : "bg-white"}`}>
                      <td className="py-3 px-4 sm:px-6 text-[#4A4A4A] font-body leading-snug">{row.label}</td>
                      <td className="py-3 px-2 sm:px-4 text-center border-l border-[#BFBFBF]/25 bg-[#008CA8]/[0.04]">
                        {row.method
                          ? <span className="inline-block text-[10px] sm:text-[11px] font-mono-tech font-medium text-[#1F3535] leading-tight">{row.active}</span>
                          : row.active
                            ? <Check className="w-4 h-4 text-[#008CA8] inline-block" strokeWidth={2.5} />
                            : <span className="text-[#BFBFBF] font-mono-tech" aria-label="non inclus">—</span>}
                      </td>
                      <td className="py-3 px-2 sm:px-4 text-center border-l border-[#BFBFBF]/25">
                        {row.method
                          ? <span className="inline-block text-[10px] sm:text-[11px] font-mono-tech font-medium text-[#C46A4A] leading-tight">{row.croissance}</span>
                          : row.croissance
                            ? <Check className="w-4 h-4 text-[#C46A4A] inline-block" strokeWidth={2.5} />
                            : <span className="text-[#BFBFBF] font-mono-tech" aria-label="non inclus">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-xs text-[#6B6B6B] mt-4 font-body">
              La Formule Croissance reprend <span className="text-[#1F3535] font-medium">tout</span> La Vitrine Active — avec une gestion du contenu via un <span className="text-[#1F3535] font-medium">Dashboard administrateur dédié</span> (au lieu du Google Sheet) — et ajoute les outils de fidélisation & d'automatisation.
            </p>
          </motion.div>

          {/* Gold separator */}
          <div className="flex items-center justify-center gap-4 mb-16" aria-hidden="true">
            <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]/50" />
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/60" />
            <span className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4AF37]/50" />
          </div>

          {/* Soft conversion bridge — quote / question CTA between plans and options */}
          <motion.div
            variants={fadeUp}
            data-testid="services-quote-cta"
            className="relative mb-16 mx-auto max-w-3xl rounded-2xl bg-white border border-[#D4AF37]/30 px-6 py-5 sm:py-4 shadow-sm flex flex-col sm:flex-row items-center gap-4 sm:gap-5 hover:border-[#D4AF37]/60 hover:shadow-md transition-all"
          >
            <div className="hidden sm:flex w-11 h-11 rounded-full bg-[#1F3535] items-center justify-center flex-shrink-0 ring-1 ring-[#D4AF37]/35">
              <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.7} />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C46A4A] font-semibold mb-0.5 nav-text">
                Pas encore prêt(e) pour la démo ?
              </p>
              <p className="text-sm text-[#1F3535] font-body">
                Posez-moi une question, ou demandez un <span className="font-playfair italic text-[#C46A4A]">devis sur-mesure</span>. Je vous réponds personnellement sous 48h.
              </p>
            </div>
            <Button
              onClick={() => document.dispatchEvent(new CustomEvent("open-contact", { detail: { message: "Bonjour, je souhaite obtenir un renseignement / un devis personnalisé sur les forfaits MonBeauSite." } }))}
              data-testid="services-quote-btn"
              className="flex-shrink-0 bg-[#1F3535] text-white hover:bg-[#0F2424] h-10 px-5 text-[11px] font-semibold tracking-[0.16em] uppercase rounded-full shadow-md shadow-[#1F3535]/20"
            >
              <Send className="w-3.5 h-3.5 mr-1.5 text-[#D4AF37]" strokeWidth={2} />
              Me contacter
            </Button>
          </motion.div>

          {/* Options */}
          <motion.div variants={fadeUp} className="mb-10 text-center">
            <span className="inline-block bg-[#D4AF37]/15 text-[#1F3535] text-xs font-semibold px-4 py-1.5 rounded-full mb-5 nav-text tracking-wider uppercase border border-[#D4AF37]/25" style={{ fontSize: "0.6rem" }}>
              Exclusivité La Formule Croissance
            </span>
            <h3 className="text-2xl sm:text-3xl font-medium text-[#1F3535] font-montserrat tracking-tight">
              Les options pour <span className="font-playfair italic text-[#C46A4A] font-normal">aller plus loin.</span>
            </h3>
          </motion.div>
          <div className="grid gap-6 mb-24 max-w-xl mx-auto">
            {options.map((s, i) => (
              <motion.div key={i} variants={fadeUp}
                onClick={() => setDetail(s)}
                data-testid={`option-card-${i}`}
                className="group relative overflow-hidden border border-[#BFBFBF]/50 rounded-2xl p-8 hover-lift bg-white hover:border-[#D4AF37]/60 cursor-pointer transition-all duration-300 shadow-sm">
                <div className="relative z-10">
                  {s.highlight && (
                    <span className="inline-flex items-center gap-1 text-[0.6rem] font-semibold bg-[#D4AF37]/15 text-[#1F3535] border border-[#D4AF37]/40 px-2.5 py-1 rounded-full nav-text tracking-wider uppercase mb-4" data-testid={`option-highlight-${i}`}>
                      <Star className="w-2.5 h-2.5 text-[#C46A4A]" /> {s.highlight}
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#C46A4A]/10 flex items-center justify-center">
                      <s.icon className="w-4 h-4 text-[#C46A4A]" strokeWidth={1.6} />
                    </div>
                    <span className="text-sm font-medium text-[#C46A4A] font-mono-tech">{s.price}</span>
                  </div>
                  <h3 className="text-lg font-medium mb-1.5 text-[#1F3535] font-montserrat tracking-tight">{s.title}</h3>
                  <p className="text-sm text-[#3F6B6B] italic mb-3 font-playfair">{s.tagline}</p>
                  <p className="text-[#4A4A4A] text-sm mb-5 leading-relaxed font-body">{s.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.features.map((f, j) => (
                      <span key={j} className="inline-flex items-center gap-1.5 text-xs bg-[#FBF9F5] border border-[#BFBFBF]/50 px-3 py-1.5 rounded-full text-[#1F3535] font-body group-hover:border-[#D4AF37]/40 transition-colors">
                        <Check className="w-3 h-3 text-[#C46A4A]" />{f}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#C46A4A] font-semibold mt-5 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 font-body tracking-wide uppercase">
                    En savoir plus <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} className="text-center mt-10 mb-4">
            <p className="text-[#6B6B6B] text-sm font-body font-light italic">
              Les options sont ajoutables à tout moment depuis votre espace client (La Formule Croissance uniquement).
            </p>
          </motion.div>

          {/* Studio Branding */}
          <motion.div variants={fadeUp} className="mb-10 text-center">
            <span className="inline-block bg-[#D4AF37]/15 text-[#1F3535] text-xs font-semibold px-4 py-1.5 rounded-full mb-5 nav-text tracking-wider uppercase border border-[#D4AF37]/25" style={{ fontSize: "0.6rem" }}>
              Le Studio Branding
            </span>
            <h3 className="text-2xl sm:text-3xl font-medium text-[#1F3535] font-montserrat tracking-tight">
              Votre ADN visuel, <span className="font-playfair italic text-[#C46A4A] font-normal">dessiné avec soin.</span>
            </h3>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {branding.filter(b => !b.hidden).map((b, i) => (
              <motion.div key={i} variants={fadeUp}
                onClick={() => setDetail(b)}
                data-testid={`branding-card-${i}`}
                className="group relative border border-[#BFBFBF]/50 rounded-2xl p-8 hover-lift bg-white text-center hover:border-[#D4AF37]/60 transition-all duration-300 shadow-sm cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-[#C46A4A]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#C46A4A]/15 transition-colors">
                  <b.icon className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.6} />
                </div>
                <h4 className="font-medium text-[#1F3535] mb-1.5 font-montserrat tracking-tight text-lg">{b.title}</h4>
                <p className="text-xl font-medium text-[#C46A4A] mb-3 font-mono-tech">{b.price}</p>
                <div className="w-8 h-px bg-[#D4AF37]/40 mx-auto mb-3" aria-hidden="true" />
                <p className="text-xs text-[#4A4A4A] leading-relaxed font-body">{b.desc}</p>
                <p className="text-xs text-[#C46A4A] font-semibold mt-5 inline-flex items-center gap-1 font-body tracking-wide uppercase group-hover:translate-x-0.5 transition-transform">
                  En savoir plus <ArrowRight className="w-3 h-3" />
                </p>
              </motion.div>
            ))}
          </div>
          </>)}
        </motion.div>
      </div>
      {detail && <DetailModal item={detail} onClose={() => setDetail(null)} onContact={onContact} />}
    </section>
  );
}

function FreelanceSection() {
  return (
    <section id="about" className="py-24 lg:py-32 relative">
      {/* Subtle textured background */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%232F4F4F' fill-opacity='1'%3E%3Cpath d='M30 30m-2 0a2 2 0 1 1 4 0a2 2 0 1 1 -4 0'/%3E%3C/g%3E%3C/svg%3E")` }} aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="max-w-4xl mx-auto"
        >
          <motion.div variants={fadeUp} className="text-center">
            <div className="inline-flex items-center justify-center mb-7">
              <img src="/logo-mbs.png" alt="MonBeauSite" className="h-20 w-20 object-cover rounded-2xl ring-1 ring-[#D4AF37]/30 shadow-md shadow-[#2F4F4F]/10" />
            </div>
            <p className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-5 inline-flex justify-center">
              03 // VOTRE AVANTAGE
            </p>
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight mb-6 text-[#1F3535] font-montserrat tracking-tight">
              Pas d'agence impersonnelle.{" "}
              <span className="font-playfair italic text-[#C46A4A] font-normal">Juste moi.</span>
            </h2>
            <p className="text-[#6B6B6B] leading-relaxed mb-12 font-body font-light max-w-2xl mx-auto">
              Je suis Emilien, développeur web freelance. Quand vous travaillez avec moi, vous avez un interlocuteur unique qui connait votre projet sur le bout des doigts. Pas de service client impersonnel, pas de ticket perdu dans la nature.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: Clock, title: "Réactivité", desc: "Je réponds à vos demandes en moins de 48h." },
              { icon: Eye, title: "Transparence", desc: "Vous savez exactement ce que vous payez, sans surprise." },
              { icon: UserRound, title: "Interlocuteur unique", desc: "De A à Z, toujours la même personne qui connaît votre projet." },
            ].map((atout, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`avantage-card-${i}`}
                className="bg-white rounded-2xl p-6 ring-1 ring-[#BFBFBF]/40 shadow-sm shadow-[#2F4F4F]/5 hover:ring-[#C46A4A]/40 hover:-translate-y-0.5 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-[#C46A4A]/10 flex items-center justify-center mb-4">
                  <atout.icon className="w-5 h-5 text-[#C46A4A]" strokeWidth={1.8} />
                </div>
                <h3 className="text-base font-semibold text-[#1F3535] mb-1.5 font-montserrat tracking-tight">{atout.title}</h3>
                <p className="text-sm text-[#6B6B6B] leading-relaxed font-body">{atout.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const testimonials = [
    {
      name: "Bilan 2 Vie",
      role: "Coaching de vie — Juillet 2026",
      text: "Un grand professionnel, particulièrement à l'écoute et investi. Il a repris mon ancien site internet, un projet pourtant complexe, pour le transformer en un site moderne, performant et parfaitement à mon image. Un immense merci pour ce superbe résultat, je le recommande les yeux fermés !",
      videoUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_web-creation-pro-2/artifacts/9r84vto7_mockup%20bilan2vie.mp4",
    },
    {
      name: "Mamz'elle Cocktail",
      role: "Bar à cocktails — Juillet 2026",
      text: "Au-delà d'un travail d'une excellence incroyable, Emilien fait preuve de disponibilité et de bienveillance — ce qui sublime cette prestation de service, car nous avons un Humain face à nous et ça… ça n'a pas de prix !",
      videoUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_web-creation-pro-2/artifacts/dap24guz_mockup%20Mamz%27elle%20Cocktail.mp4",
    },
    {
      name: "Le Privé Coiffeur",
      role: "Salon de coiffure — Juin 2026",
      text: "Merci pour le travail réalisé, largement à la hauteur de mes attentes.",
      videoUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_web-creation-pro-2/artifacts/kzreza0c_Mockup%20Le%20Prive%CC%81.mp4",
    },
  ];

  return (
    <section id="temoignages" className="py-24 lg:py-32 bg-[#F2EEE8]/60">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="section-num font-mono-tech text-xs tracking-widest text-[#3F6B6B] mb-5 inline-flex">
              04 // TÉMOIGNAGES
            </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-medium mb-4 text-[#2F4F4F] font-montserrat tracking-tight">
            Leurs sites, <span className="font-playfair italic text-[#C46A4A] font-normal">leurs mots.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#6B6B6B] font-body mb-14 max-w-2xl text-sm leading-relaxed">
            Chaque site est unique, pensé pour l'univers de la personne qu'il représente. Ci-dessous, trois projets récents et les mots que ces professionnels ont pris le temps de partager sur Google.
          </motion.p>

          <div className="space-y-14 lg:space-y-20">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`portfolio-item-${i}`}
                className={`grid md:grid-cols-5 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
              >
                {/* Mockup video — square native format, capped width */}
                <div className="md:col-span-3">
                  <div
                    className="relative rounded-2xl overflow-hidden bg-[#1F3535] shadow-2xl shadow-[#1F3535]/25 aspect-square max-w-sm mx-auto"
                    data-testid={`portfolio-video-wrapper-${i}`}
                  >
                    <video
                      src={t.videoUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-contain"
                      data-testid={`portfolio-video-${i}`}
                    />
                  </div>
                </div>

                {/* Testimonial */}
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-[#6B6B6B] nav-text">Avis Google vérifié</span>
                  </div>
                  <p className="text-[#2F4F4F] text-base lg:text-lg leading-relaxed mb-6 italic font-playfair">
                    « {t.text} »
                  </p>
                  <div className="pt-5 border-t border-[#BFBFBF]">
                    <p className="text-base font-semibold text-[#2F4F4F] font-montserrat">{t.name}</p>
                    <p className="text-xs text-[#6B6B6B] font-body mt-0.5">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} className="mt-16 text-center">
            <p className="text-xs text-[#6B6B6B] font-body italic">
              Envie de voir votre univers prendre vie de la même façon ?{" "}
              <a href="/demande-de-demo" className="text-[#C46A4A] underline underline-offset-4 hover:text-[#A6553A]">
                Demandez votre démo gratuite
              </a>.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export function FAQSection() {
  const picks = LANDING_FAQ_PICKS.map((p) => ({ section: p.sectionId, item: getFaqPick(p) })).filter((p) => p.item);

  const renderAnswer = (text) => renderFaqAnswer(text, React);

  return (
    <section id="faq" className="py-24 lg:py-32 bg-[#FBF9F5] relative overflow-hidden">
      {/* Decorative gold thread */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 flex items-center gap-4 opacity-50" aria-hidden="true">
        <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/70" />
        <span className="h-px w-16 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
      </div>

      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="section-num font-mono-tech text-xs tracking-widest text-[#3F6B6B] mb-5 flex justify-center">
            05 // FAQ
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-medium mb-4 text-[#1F3535] font-montserrat tracking-tight text-center leading-tight">
            Les réponses <span className="font-playfair italic text-[#C46A4A] font-normal">qui comptent.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#6B6B6B] text-sm sm:text-base font-body text-center max-w-2xl mx-auto mb-12 leading-relaxed">
            Les questions les plus fréquentes pour démarrer sereinement. Pour tout le reste, une FAQ complète est à votre disposition.
          </motion.p>

          <motion.div variants={fadeUp}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="landing-faq-accordion">
              {picks.map((p, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="bg-white border border-[#BFBFBF]/50 rounded-xl overflow-hidden data-[state=open]:border-[#D4AF37]/60 data-[state=open]:shadow-sm transition-all"
                >
                  <AccordionTrigger
                    className="px-6 py-5 text-left text-[#1F3535] font-medium font-display text-base sm:text-[1.05rem] hover:no-underline hover:bg-[#FBF9F5]/50 transition-colors [&>svg]:text-[#C46A4A]"
                    data-testid={`landing-faq-question-${i}`}
                  >
                    <span className="flex-1 pr-4">{p.item.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-1 border-t border-[#D4AF37]/15">
                    <div className="pt-4">{renderAnswer(p.item.a)}</div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* CTAs : all questions + direct contact */}
          <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/faq"
              data-testid="faq-see-all-btn"
              className="inline-flex items-center gap-2 bg-[#2F4F4F] text-white hover:bg-[#1F3535] px-6 py-3 rounded-full text-sm font-semibold font-body nav-text tracking-wide uppercase group transition-colors"
            >
              Voir toutes les questions
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={() => document.dispatchEvent(new CustomEvent('open-contact'))}
              data-testid="faq-contact-btn"
              className="inline-flex items-center gap-2 text-[#C46A4A] hover:text-[#A6553A] font-semibold text-sm font-body nav-text tracking-wide uppercase group transition-colors"
            >
              <Mail className="w-4 h-4" />
              Écrivez-moi
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}



export function FinalCTA({ onContact }) {
  return (
    <section className="py-24 lg:py-32 bg-[#1F3535] relative overflow-hidden">
      {/* Decorative gold accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.06),transparent_70%)]" aria-hidden="true" />
      <div className="max-w-3xl mx-auto px-6 text-center relative">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="mb-10">
            <img src={LOGO_BLANC_URL} alt="MonBeauSite" className="h-20 mx-auto object-contain drop-shadow-lg" />
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight font-montserrat tracking-tight">
            Prêt à donner une{" "}
            <span className="font-playfair italic text-[#E8D8A9] font-normal">nouvelle dimension</span>{" "}
            à votre entreprise ?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/70 mb-10 max-w-md mx-auto leading-relaxed font-body font-light">
            Contactez-moi pour un échange gratuit et sans engagement. En 15 minutes, je vous montre ce que votre site pourrait devenir.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
            <Button onClick={onContact} data-testid="final-cta-email" className="bg-[#C46A4A] text-white hover:bg-[#A6553A] h-12 px-8 text-sm font-semibold tracking-wide uppercase shadow-lg shadow-[#C46A4A]/30">
              <Send className="mr-2 w-4 h-4" />
              Lancer ma demo gratuite
            </Button>
            <Button
              variant="outline"
              onClick={() => document.dispatchEvent(new CustomEvent("open-contact", { detail: { message: "Bonjour, je souhaite obtenir un renseignement / un devis sur les forfaits MonBeauSite." } }))}
              data-testid="final-cta-quote"
              className="border-[#D4AF37]/40 bg-white/5 text-white hover:bg-white/10 hover:border-[#D4AF37]/70 h-12 px-8 text-sm font-medium tracking-wide uppercase"
            >
              <Mail className="mr-2 w-4 h-4 text-[#D4AF37]" strokeWidth={1.7} />
              Une question, un devis ?
            </Button>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-6 flex items-center justify-center gap-2 text-xs text-white/55 font-body">
            <span className="h-px w-8 bg-white/20" aria-hidden="true" />
            <span className="uppercase tracking-[0.2em]">ou directement par téléphone</span>
            <span className="h-px w-8 bg-white/20" aria-hidden="true" />
          </motion.div>
          <motion.div variants={fadeUp} className="mt-3 text-center">
            <a href="tel:+33652151096" className="inline-flex items-center gap-2 text-white/85 hover:text-[#D4AF37] transition-colors font-mono-tech text-base">
              <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.7} />
              06 52 15 10 96
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#BFBFBF] py-12 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col items-center gap-6">
          <img src={LOGO_URL} alt="MonBeauSite" className="h-8 opacity-40" />
          <div className="flex flex-wrap justify-center gap-6 text-sm font-body">
            <a href="/#about" className="text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors">A propos</a>
            <Link to="/services-et-tarifs" className="text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors">Services & Tarifs</Link>
            <button onClick={() => document.dispatchEvent(new CustomEvent('open-contact'))} className="text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors">Contact</button>
            <Link to="/blog" className="text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors">Blog</Link>
          </div>
          <div className="flex flex-col items-center gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-[#6B6B6B]/70 font-body">Suivez-nous</span>
            <SocialLinks variant="light" />
          </div>
          <div className="border-t border-[#BFBFBF] w-full pt-5 flex flex-wrap justify-center gap-x-2 gap-y-1 text-xs text-[#6B6B6B]/50 font-body">
            <span>&copy; {new Date().getFullYear()} MonBeauSite</span>
            <span className="hidden sm:inline">&mdash;</span>
            <Link to="/mentions-legales" className="hover:text-[#2F4F4F] transition-colors" data-testid="footer-mentions-link">Mentions legales</Link>
            <span>&mdash;</span>
            <Link to="/politique-confidentialite" className="hover:text-[#2F4F4F] transition-colors" data-testid="footer-privacy-link">Politique de confidentialite</Link>
            <span>&mdash;</span>
            <Link to="/cgv" className="hover:text-[#2F4F4F] transition-colors" data-testid="footer-cgv-link">CGV</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}


export function ContactModal({ open, onClose, prefillMessage }) {
  const [form, setForm] = useState({ nom_entreprise: "", nom_responsable: "", email: "", telephone: "", message: "", consent_contact: false, consent_marketing: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Prefill message when modal opens with a payload
  useEffect(() => {
    if (open && prefillMessage) {
      setForm(f => ({ ...f, message: prefillMessage }));
    }
  }, [open, prefillMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue.");
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setSent(false); setForm({ nom_entreprise: "", nom_responsable: "", email: "", telephone: "", message: "", consent_contact: false, consent_marketing: false }); }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl border border-[#BFBFBF] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-7 pt-7 pb-0">
              <button onClick={handleClose} className="absolute top-4 right-4 text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-[#2F4F4F]/10 flex items-center justify-center mx-auto mb-4">
                  <Send className="w-5 h-5 text-[#2F4F4F]" />
                </div>
                <h2 className="text-xl font-medium text-[#1F3535] font-montserrat tracking-tight" data-testid="contact-modal-title">
                  {sent
                    ? "Demande envoyée !"
                    : prefillMessage
                      ? <>Une <span className="font-playfair italic text-[#C46A4A] font-normal">question</span>, un devis ?</>
                      : <>Lancez votre <span className="font-playfair italic text-[#C46A4A] font-normal">démo gratuite</span></>}
                </h2>
                {!sent && (
                  <p className="text-xs text-[#6B6B6B] mt-2 leading-relaxed font-body">
                    {prefillMessage
                      ? "Précisez votre besoin ci-dessous. Nous revenons vers vous personnellement sous 48h."
                      : "Parlez-nous de votre projet. On revient vers vous rapidement."}
                  </p>
                )}
              </div>
            </div>

            {sent ? (
              <div className="px-7 pb-7 text-center">
                <div className="w-16 h-16 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-7 h-7 text-[#3F6B6B]" />
                </div>
                <p className="text-sm text-[#333333] leading-relaxed mb-2">
                  Merci ! Votre demande a bien ete envoyee.
                </p>
                <p className="text-xs text-[#6B6B6B] mb-6">
                  Nous vous recontactons dans les plus brefs delais.
                </p>
                <Button onClick={handleClose} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] h-10 px-8 rounded-full text-xs font-medium tracking-wide uppercase">
                  Fermer
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-7 pb-7 space-y-3.5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Entreprise *</Label>
                    <Input
                      data-testid="contact-nom-entreprise"
                      value={form.nom_entreprise} onChange={(e) => update("nom_entreprise", e.target.value)}
                      className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-10 text-sm text-[#333333] placeholder:text-[#999]"
                      placeholder="Votre entreprise" required
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Responsable *</Label>
                    <Input
                      data-testid="contact-nom-responsable"
                      value={form.nom_responsable} onChange={(e) => update("nom_responsable", e.target.value)}
                      className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-10 text-sm text-[#333333] placeholder:text-[#999]"
                      placeholder="Votre nom" required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Email *</Label>
                  <Input
                    data-testid="contact-email"
                    type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                    className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-10 text-sm text-[#333333] placeholder:text-[#999]"
                    placeholder="votre@email.com" required
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Telephone</Label>
                  <Input
                    data-testid="contact-telephone"
                    type="tel" value={form.telephone} onChange={(e) => update("telephone", e.target.value)}
                    className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-10 text-sm text-[#333333] placeholder:text-[#999]"
                    placeholder="06 00 00 00 00"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Votre projet en quelques mots</Label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message} onChange={(e) => update("message", e.target.value)}
                    className="mt-1 w-full bg-[#FBF9F5] border border-[#BFBFBF] rounded-md h-20 px-3 py-2 text-sm text-[#333333] placeholder:text-[#999] resize-none focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/20 focus:border-[#2F4F4F]/40"
                    placeholder="Decrivez brievement votre activite et ce que vous recherchez..."
                  />
                </div>
                {/* Consent checkboxes */}
                <div className="space-y-2.5 pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <input type="checkbox" checked={form.consent_contact} onChange={(e) => update("consent_contact", e.target.checked)}
                      data-testid="contact-consent-contact"
                      className="mt-0.5 w-4 h-4 rounded border-[#BFBFBF] text-[#2F4F4F] focus:ring-[#2F4F4F]/30 accent-[#2F4F4F] flex-shrink-0" />
                    <span className="text-[11px] text-[#6B6B6B] leading-relaxed group-hover:text-[#333333] transition-colors">
                      J'accepte que mes données soient utilisées pour être recontacté(e) au sujet de mon projet. *
                      <Link to="/politique-confidentialite" className="text-[#2F4F4F] underline ml-0.5" onClick={(e) => e.stopPropagation()}>Politique de confidentialité</Link>
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <input type="checkbox" checked={form.consent_marketing} onChange={(e) => update("consent_marketing", e.target.checked)}
                      data-testid="contact-consent-marketing"
                      className="mt-0.5 w-4 h-4 rounded border-[#BFBFBF] text-[#2F4F4F] focus:ring-[#2F4F4F]/30 accent-[#2F4F4F] flex-shrink-0" />
                    <span className="text-[11px] text-[#6B6B6B] leading-relaxed group-hover:text-[#333333] transition-colors">
                      J'accepte de recevoir les conseils d'expert pour optimiser ma présence en ligne et faire grandir mon activité (facultatif).
                    </span>
                  </label>
                </div>
                <Button
                  data-testid="contact-submit"
                  type="submit" disabled={loading || !form.consent_contact}
                  className="w-full bg-[#C46A4A] text-white hover:bg-[#A6553A] h-11 rounded-full font-semibold tracking-wide uppercase text-xs shadow-md shadow-[#C46A4A]/20"
                >
                  {loading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <><Send className="w-4 h-4 mr-2" /> {prefillMessage ? "Envoyer ma demande" : "Lancer ma démo gratuite"}</>}
                </Button>
                <p className="text-center text-[10px] text-[#999] pt-0.5 font-body">
                  Réponse rapide. Sans aucun engagement.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
